import cn from 'classnames';
import React, { useMemo } from 'react';

import getDesignData from 'editor/src/store/design/selector/getDesignData';
import isAutofillAllowed from 'editor/src/store/editor/selector/isAutofillAllowed';
import fillPdfPagesOperation from 'editor/src/store/gallery/operation/fillPdfPagesOperation';
import getPdfSplits from 'editor/src/store/gallery/selector/getPdfSplits';
import { dismissPdfSplitAction } from 'editor/src/store/gallery/slice';
import { PdfSplitProgress } from 'editor/src/store/gallery/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import getPdfPagePlacement from 'editor/src/util/autofill/getPdfPagePlacement';
import useTr from 'editor/src/util/useTr';

import Button from 'editor/src/component/Button';
import IconCross from 'editor/src/component/Icon/IconCross';
import IconLoading from 'editor/src/component/Icon/IconLoading';

import styles from './index.module.scss';

const isRunning = (split: PdfSplitProgress) => split.status === 'splitting' || split.status === 'saving';

interface RunningCardProps {
  title: string;
  saved: number;
  // unknown until every PDF it stands for is split
  total?: number;
}

function RunningCard({ title, saved, total }: RunningCardProps) {
  const tr = useTr();
  const percent = total ? Math.round((saved / total) * 100) : undefined;
  return (
    <div className={cn(styles.item, 'cy-pdf-split-running')}>
      <div className={styles.progressBackground}>
        {percent === undefined ? (
          <div className={styles.progressBarInfinite} />
        ) : (
          <div className={styles.progressBar} style={{ width: `${percent}%` }} />
        )}
      </div>
      <div className={styles.row}>
        <div className={styles.icon}>
          <IconLoading />
        </div>
        <div className={styles.captions}>
          <div className={styles.title}>{title}</div>
          <div className={cn(styles.detail, 'cy-pdf-split-detail')}>
            {total
              ? tr('editor-pdf-split-pages-ready', '{{saved}} of {{total}} pages ready', { saved, total })
              : tr('editor-pdf-split-explanation', 'Each page of the PDF becomes one image, in page order.')}
          </div>
        </div>
      </div>
    </div>
  );
}

// Uploading one PDF per page splits many PDFs at once, which would otherwise stack a card for each.
function RunningGroupCard({ splits }: { splits: PdfSplitProgress[] }) {
  const tr = useTr();
  const allSplit = splits.every((split) => split.status === 'saving' && split.pagesTotal);
  return (
    <RunningCard
      title={tr('editor-pdf-split-splitting-many', 'Splitting {{count}} PDFs into pages', { count: splits.length })}
      saved={splits.reduce((sum, split) => sum + (split.pagesSaved ?? 0), 0)}
      total={allSplit ? splits.reduce((sum, split) => sum + (split.pagesTotal ?? 0), 0) : undefined}
    />
  );
}

interface ItemProps {
  split: PdfSplitProgress;
}

function PdfSplitItem({ split }: ItemProps) {
  const tr = useTr();
  const dispatch = useDispatch();
  const autofillAllowed = useSelector(isAutofillAllowed);
  const designData = useSelector(getDesignData);

  const name = split.fileName;
  const pageImageIds = split.pageImageIds ?? [];
  const total = split.pagesTotal ?? pageImageIds.length;
  const saved = split.pagesSaved ?? 0;
  const missingPdfPages = pageImageIds.flatMap((imageId, index) => (imageId ? [] : [index + 1]));
  const addedPages = total - missingPdfPages.length;
  // Page counts only matter to Fill, which hosts without autofill (apparel, for one) do not offer.
  const placement = useMemo(
    () => (designData && autofillAllowed ? getPdfPagePlacement(designData, split.pageImageIds ?? []) : undefined),
    [designData, autofillAllowed, split.pageImageIds],
  );
  const skippedPdfPages = placement?.skippedPdfPages ?? [];
  const designedPdfPages = placement?.designedPdfPages ?? [];
  const productPages = placement?.productPages ?? 0;
  const dismiss = () => dispatch(dismissPdfSplitAction(split.uploaderId));
  const dismissButton = (
    <button
      type="button"
      className={cn(styles.dismiss, 'cy-pdf-split-dismiss')}
      onClick={dismiss}
      aria-label={tr('editor-pdf-split-dismiss', 'Dismiss')}
    >
      <IconCross />
    </button>
  );

  if (isRunning(split)) {
    return (
      <RunningCard
        title={tr('editor-pdf-split-splitting', 'Splitting {{name}} into pages', { name })}
        saved={saved}
        total={split.status === 'saving' ? total : undefined}
      />
    );
  }

  if (split.status === 'failed') {
    return (
      <div className={cn(styles.item, styles.failed, 'cy-pdf-split-failed')}>
        <div className={styles.row}>
          <div className={styles.captions}>
            <div className={styles.title}>
              {tr('editor-pdf-split-failed', "Couldn't split {{name}} into pages", { name })}
            </div>
            <div className={styles.detail}>
              {tr('editor-pdf-split-failed-hint', 'Upload it again, or upload its pages as separate files.')}
            </div>
          </div>
          {dismissButton}
        </div>
      </div>
    );
  }

  return (
    <div className={cn(styles.item, 'cy-pdf-split-done')}>
      <div className={styles.row}>
        <div className={styles.captions}>
          <div className={styles.title}>
            {addedPages === 1
              ? tr('editor-pdf-split-done-one', '1 page from {{name}} added', { name })
              : tr('editor-pdf-split-done', '{{count}} pages from {{name}} added in page order', {
                  count: addedPages,
                  name,
                })}
          </div>
          {!!split.unplacedPdfPages?.length && (
            <div className={cn(styles.detail, styles.mismatch, 'cy-pdf-split-unplaced')}>
              {tr(
                'editor-pdf-split-unplaced-pages',
                "Fill left PDF pages {{pages}} out because they aren't available in Files. Fill again once they are.",
                { pages: split.unplacedPdfPages.join(', ') },
              )}
            </div>
          )}
          {!!missingPdfPages.length && (
            <div className={cn(styles.detail, styles.mismatch, 'cy-pdf-split-missing')}>
              {tr(
                'editor-pdf-split-missing-pages',
                "PDF pages {{pages}} couldn't be saved. Upload them as separate files to add them.",
                { pages: missingPdfPages.join(', ') },
              )}
            </div>
          )}
          {!!skippedPdfPages.length && (
            <div className={cn(styles.detail, 'cy-pdf-split-skipped')}>
              {tr(
                'editor-pdf-split-unprinted-pages',
                "PDF pages {{pages}} land on pages that aren't printed, so Fill leaves them out.",
                { pages: skippedPdfPages.join(', ') },
              )}
            </div>
          )}
          {!!designedPdfPages.length && (
            <div className={cn(styles.detail, 'cy-pdf-split-designed')}>
              {tr(
                'editor-pdf-split-designed-pages',
                'PDF pages {{pages}} land on pages that already have a design, so Fill leaves those pages as they are.',
                { pages: designedPdfPages.join(', ') },
              )}
            </div>
          )}
          {!skippedPdfPages.length && !!productPages && productPages !== total && (
            <div className={cn(styles.detail, styles.mismatch, 'cy-pdf-split-mismatch')}>
              {tr(
                'editor-pdf-split-page-count-mismatch',
                'The PDF has {{pdfPages}} pages and this product has {{productPages}}.',
                {
                  pdfPages: total,
                  productPages,
                },
              )}
            </div>
          )}
        </div>
        {dismissButton}
      </div>
      {autofillAllowed && (
        <Button
          secondary
          small
          fullWidth
          className={cn(styles.fill, 'cy-pdf-split-fill')}
          onClick={() => dispatch(fillPdfPagesOperation(split))}
        >
          {tr('editor-pdf-split-fill', 'Fill product pages')}
        </Button>
      )}
    </div>
  );
}

function PdfSplitStatus() {
  const allSplits = useSelector(getPdfSplits);
  const splits = useMemo(() => allSplits.filter((split) => !split.dismissed), [allSplits]);
  const running = splits.filter(isRunning);
  if (!splits.length) {
    return null;
  }

  return (
    <div className={cn(styles.pdfSplitStatus, 'cy-pdf-split-status')}>
      {running.length > 1 && <RunningGroupCard splits={running} />}
      {splits
        .filter((split) => running.length === 1 || !isRunning(split))
        .map((split) => (
          <PdfSplitItem key={split.uploaderId} split={split} />
        ))}
    </div>
  );
}

export default React.memo(PdfSplitStatus);
